import * as React from "react";
import type { PresentationData } from "@/data/types";
import { PersonalHero } from "@/components/presentation/PersonalHero";
import { PropertySnapshot } from "@/components/presentation/PropertySnapshot";
import { PropertyGallery } from "@/components/presentation/PropertyGallery";
import { AnalysisSection } from "@/components/presentation/AnalysisSection";
import { ListingJourney } from "@/components/presentation/ListingJourney";
import { OpportunitySection } from "@/components/presentation/OpportunitySection";
import { RelaunchStrategy } from "@/components/presentation/RelaunchStrategy";
import { LaunchTimeline } from "@/components/presentation/LaunchTimeline";
import { AgentRecommendation } from "@/components/presentation/AgentRecommendation";
import { AboutAlain } from "@/components/presentation/AboutAlain";
import { TeamCredibility } from "@/components/presentation/TeamCredibility";
import { ContactCTA } from "@/components/presentation/ContactCTA";
import { PresentationFooter } from "@/components/presentation/PresentationFooter";
import { withApprovedAgentCredentials } from "@/data/approvedAgentProfile";

interface Props {
  data: PresentationData;
}

// SELLER_RELAUNCH template. Hierarchy:
// Hero → What the market saw → Price history → What I noticed →
// What I would change → Positioning → Visuals → First 14 days →
// About Alain → Team credibility → Recommendation → CTA
export const SellerRelaunchTemplate: React.FC<Props> = ({ data }) => {
  const agent = withApprovedAgentCredentials(data.agent);

  return (
    <div data-testid="seller-relaunch-template">
      <PersonalHero data={{ ...data, agent }} />
      <PropertySnapshot property={data.property} snapshot={data.snapshot} />
      {data.listingHistory && <ListingJourney history={data.listingHistory} />}
      {data.analysis && <AnalysisSection analysis={data.analysis} />}
      {data.strategy && <RelaunchStrategy strategy={data.strategy} />}
      {data.opportunity && <OpportunitySection opportunity={data.opportunity} />}
      <PropertyGallery
        images={data.property.images}
        floorPlan={data.property.floorPlan}
      />
      {data.launchPlan && <LaunchTimeline plan={data.launchPlan} />}
      <AboutAlain agent={agent} />
      <TeamCredibility />
      {data.recommendation && (
        <AgentRecommendation
          recommendation={data.recommendation}
          agent={agent}
        />
      )}
      <ContactCTA
        agent={agent}
        headline={data.contact?.headline}
        body={data.contact?.body}
      />
      <PresentationFooter agent={agent} />
    </div>
  );
};
